import { useExpandHeight } from "@redotech/react-animation/transition";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import TriangleDown from "@redotech/redo-icons/triangle-down_filled.svg";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  EnterKey,
} from "@redotech/web-util/key";
import classNames from "classnames";
import {
  KeyboardEvent as ReactKeyboardEvent,
  ReactNode,
  memo,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { RedoBadge } from "../badge/redo-badge";
import { RedoCheckbox } from "../checkbox/redo-checkbox";
import { RedoIcon } from "../icon/redo-icon";
import {
  RedoCheckboxTreeGroup,
  RedoCheckboxTreeItem,
  RedoCheckboxTreeNode,
  ariaCheckedValue,
  countSelectedInGroup,
  expandAllGroupIds,
  filterCheckboxTree,
  findTypeaheadIndex,
  firstSelectedNodeId,
  flattenVisibleTreeNodes,
  getGroupCheckboxValue,
  groupNodeId,
  itemNodeId,
  itemsByIdMap,
  toggleExpandedGroupIds,
  treeNodeLabel,
  withItemIdsSelection,
  withItemSelection,
} from "./redo-checkbox-tree-logic";
import * as css from "./redo-checkbox-tree.module.css";

const TYPEAHEAD_MS = 500;
const zeroHeight = { height: "0px", display: "none" };

const CheckboxTreeGroupChildren = memo(function CheckboxTreeGroupChildren({
  children,
  expanded,
}: {
  children: ReactNode;
  expanded: boolean;
}) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [animateStyles, animating] = useExpandHeight(expanded, container);
  const style = !animating && !expanded ? zeroHeight : animateStyles;

  return (
    <Flex
      className={classNames(css.children, expanded && !animating && css.open)}
      dir="column"
      gap="xs"
      pl="3xl"
      pt={expanded ? "xs" : undefined}
      ref={setContainer}
      role="group"
      style={style}
    >
      {(animating || expanded) && children}
    </Flex>
  );
});

export interface RedoCheckboxTreeProps {
  "data-dd-action-name"?: string;
  items: readonly RedoCheckboxTreeItem[];
  groups: readonly RedoCheckboxTreeGroup[];
  selectedIds: readonly string[];
  setSelectedIds: (ids: string[]) => void;
  searchString?: string;
  expandedGroupIds?: readonly string[];
  setExpandedGroupIds?: (ids: string[]) => void;
  disabledIds?: readonly string[];
  emptyListMessage?: string;
  ariaLabel?: string;
  keyboardTarget?: HTMLElement | null;
}

export const RedoCheckboxTree = memo(function RedoCheckboxTree({
  items,
  groups,
  selectedIds,
  setSelectedIds,
  searchString = "",
  expandedGroupIds: expandedGroupIdsProp,
  setExpandedGroupIds: setExpandedGroupIdsProp,
  disabledIds,
  emptyListMessage: emptyListMessageProp,
  ariaLabel: ariaLabelProp,
  keyboardTarget,
  "data-dd-action-name": actionName,
}: RedoCheckboxTreeProps) {
  const { t } = useTranslation();
  const ariaLabel = ariaLabelProp ?? t("Checkbox tree", { context: "list" });
  const emptyListMessage =
    emptyListMessageProp ?? t("No items", { context: "empty list" });
  const reactId = useId();
  const treeRef = useRef<HTMLDivElement | null>(null);
  const typeaheadRef = useRef<{
    query: string;
    timeout: ReturnType<typeof setTimeout> | undefined;
  }>({ query: "", timeout: undefined });
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState<string[]>(
    [],
  );
  const [activeNodeId, setActiveNodeId] = useState<string | undefined>(
    undefined,
  );

  const expandedIds = expandedGroupIdsProp ?? uncontrolledExpanded;
  const setExpandedIds = setExpandedGroupIdsProp ?? setUncontrolledExpanded;

  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);
  const disabledIdSet = useMemo(
    () => new Set(disabledIds ?? []),
    [disabledIds],
  );
  const itemsById = useMemo(() => itemsByIdMap(items), [items]);
  const searching = searchString.trim().length > 0;

  const { visibleGroups } = useMemo(
    () => filterCheckboxTree(items, groups, searchString),
    [items, groups, searchString],
  );

  const expandedSet = useMemo(() => new Set(expandedIds), [expandedIds]);

  const visibleNodes = useMemo(
    () => flattenVisibleTreeNodes(visibleGroups, expandedSet, searching),
    [visibleGroups, expandedSet, searching],
  );

  const commitSelected = useHandler((next: Set<string>) => {
    setSelectedIds([...next]);
  });

  const commitExpanded = useHandler((next: Set<string>) => {
    setExpandedIds([...next]);
  });

  const nodeDomId = useHandler((nodeId: string) => `${reactId}-${nodeId}`);

  const moveFocusToTree = useHandler(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    treeRef.current?.focus();
  });

  useEffect(() => {
    if (visibleNodes.length === 0) {
      setActiveNodeId(undefined);
      return;
    }
    if (!visibleNodes.some((node) => node.nodeId === activeNodeId)) {
      setActiveNodeId(firstSelectedNodeId(visibleNodes, selected));
    }
  }, [visibleNodes, activeNodeId, selected]);

  useEffect(() => {
    if (!activeNodeId) {
      return;
    }
    document
      .getElementById(nodeDomId(activeNodeId))
      ?.scrollIntoView({ block: "nearest" });
  }, [activeNodeId, nodeDomId]);

  const toggleGroupExpanded = useHandler(
    (groupId: string, expanded?: boolean) => {
      commitExpanded(toggleExpandedGroupIds(expandedSet, groupId, expanded));
    },
  );

  const toggleNodeChecked = useHandler((node: RedoCheckboxTreeNode) => {
    if (node.kind === "group") {
      const itemIds = node.items.map((item) => item.id);
      const nextChecked =
        getGroupCheckboxValue(itemIds, selected, itemsById, disabledIdSet) !==
        true;
      commitSelected(
        withItemIdsSelection(
          selected,
          itemIds,
          nextChecked,
          itemsById,
          disabledIdSet,
        ),
      );
      return;
    }
    if (node.item.disabled || disabledIdSet.has(node.item.id)) {
      return;
    }
    commitSelected(
      withItemSelection(selected, node.item.id, !selected.has(node.item.id)),
    );
  });

  const handleKey = useHandler(
    (event: KeyboardEvent | ReactKeyboardEvent, fromSearch: boolean) => {
      if (visibleNodes.length === 0) {
        return;
      }

      const navigationKeys: ReadonlySet<string> = new Set([
        ArrowDown,
        ArrowUp,
        ArrowLeft,
        ArrowRight,
        "Home",
        "End",
      ]);
      if (fromSearch && !navigationKeys.has(event.key)) {
        return;
      }

      let activeIndex = visibleNodes.findIndex(
        (node) => node.nodeId === activeNodeId,
      );
      if (activeIndex < 0) {
        event.preventDefault();
        const startIndex = event.key === ArrowUp ? visibleNodes.length - 1 : 0;
        const start = visibleNodes[startIndex];
        if (start) {
          setActiveNodeId(start.nodeId);
        }
        if (fromSearch) {
          moveFocusToTree();
        }
        if (
          event.key === ArrowDown ||
          event.key === ArrowUp ||
          event.key === "Home" ||
          event.key === "End"
        ) {
          return;
        }
        activeIndex = startIndex;
      }
      const active = visibleNodes[activeIndex];
      if (!active) {
        return;
      }

      switch (event.key) {
        case ArrowDown: {
          event.preventDefault();
          const next =
            visibleNodes[Math.min(activeIndex + 1, visibleNodes.length - 1)];
          if (next) {
            setActiveNodeId(next.nodeId);
          }
          if (fromSearch) {
            moveFocusToTree();
          }
          break;
        }
        case ArrowUp: {
          event.preventDefault();
          const previous = visibleNodes[Math.max(activeIndex - 1, 0)];
          if (previous) {
            setActiveNodeId(previous.nodeId);
          }
          if (fromSearch) {
            moveFocusToTree();
          }
          break;
        }
        case ArrowRight: {
          event.preventDefault();
          if (active.kind !== "group") {
            break;
          }
          if (!active.expanded) {
            toggleGroupExpanded(active.group.id, true);
            break;
          }
          const child = visibleNodes[activeIndex + 1];
          if (child?.kind === "item" && child.groupId === active.group.id) {
            setActiveNodeId(child.nodeId);
          }
          break;
        }
        case ArrowLeft: {
          event.preventDefault();
          if (active.kind === "group") {
            if (active.expanded && !searching) {
              toggleGroupExpanded(active.group.id, false);
            }
            break;
          }
          setActiveNodeId(groupNodeId(active.groupId));
          break;
        }
        case "Home": {
          event.preventDefault();
          setActiveNodeId(visibleNodes[0]?.nodeId);
          break;
        }
        case "End": {
          event.preventDefault();
          setActiveNodeId(visibleNodes[visibleNodes.length - 1]?.nodeId);
          break;
        }
        case " ": {
          event.preventDefault();
          toggleNodeChecked(active);
          break;
        }
        case EnterKey: {
          event.preventDefault();
          if (active.kind === "group") {
            if (!searching) {
              toggleGroupExpanded(active.group.id);
            }
            break;
          }
          toggleNodeChecked(active);
          break;
        }
        case "*": {
          event.preventDefault();
          commitExpanded(expandAllGroupIds(visibleGroups));
          break;
        }
        default: {
          if (
            (event.key === "a" || event.key === "A") &&
            (event.ctrlKey || event.metaKey)
          ) {
            event.preventDefault();
            const visibleItemIds = visibleGroups.flatMap(
              ({ items: groupItems }) => groupItems.map((item) => item.id),
            );
            commitSelected(
              withItemIdsSelection(
                selected,
                visibleItemIds,
                true,
                itemsById,
                disabledIdSet,
              ),
            );
            break;
          }
          if (
            event.key.length === 1 &&
            !event.ctrlKey &&
            !event.metaKey &&
            !event.altKey
          ) {
            const buffer = typeaheadRef.current;
            if (buffer.timeout !== undefined) {
              clearTimeout(buffer.timeout);
            }
            buffer.query += event.key;
            buffer.timeout = setTimeout(() => {
              buffer.query = "";
              buffer.timeout = undefined;
            }, TYPEAHEAD_MS);
            const match = findTypeaheadIndex(
              visibleNodes.map(treeNodeLabel),
              buffer.query,
              activeIndex,
            );
            if (match !== undefined) {
              const matched = visibleNodes[match];
              if (matched) {
                setActiveNodeId(matched.nodeId);
              }
            }
          }
        }
      }
    },
  );

  const handleTreeKeyDown = useHandler((event: ReactKeyboardEvent) => {
    handleKey(event, false);
  });

  useEffect(() => {
    if (!keyboardTarget) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      handleKey(event, true);
    };
    keyboardTarget.addEventListener("keydown", onKeyDown);
    return () => keyboardTarget.removeEventListener("keydown", onKeyDown);
  }, [keyboardTarget, handleKey]);

  if (visibleGroups.length === 0) {
    return (
      <Flex
        data-arbiter="RedoCheckboxTree"
        data-dd-action-name={actionName}
        py="md"
        w="full"
      >
        <Text fontSize="sm" textColor="secondary">
          {emptyListMessage}
        </Text>
      </Flex>
    );
  }

  const activeDomId = activeNodeId ? nodeDomId(activeNodeId) : undefined;

  return (
    <Flex
      aria-activedescendant={activeDomId}
      aria-label={ariaLabel}
      aria-multiselectable="true"
      className={css.tree}
      data-arbiter="RedoCheckboxTree"
      data-dd-action-name={actionName}
      dir="column"
      gap="sm"
      onKeyDown={handleTreeKeyDown}
      ref={treeRef}
      role="tree"
      tabIndex={0}
      w="full"
    >
      {visibleGroups.map(({ group, items: groupItems }, index) => {
        const previousSection = visibleGroups[index - 1]?.group.section;
        const showSectionHeader =
          group.section !== undefined && group.section !== previousSection;
        const expanded = searching || expandedSet.has(group.id);
        const itemIds = groupItems.map((item) => item.id);
        const groupValue = getGroupCheckboxValue(
          itemIds,
          selected,
          itemsById,
          disabledIdSet,
        );
        const selectedCount = countSelectedInGroup(itemIds, selected);
        const groupNode = groupNodeId(group.id);
        const groupDisabled = groupItems.every(
          (item) => item.disabled || disabledIdSet.has(item.id),
        );

        return (
          <Flex dir="column" gap="none" key={group.id} w="full">
            {showSectionHeader && group.section && (
              <Flex
                className={css.sectionHeader}
                mt={index > 0 ? "xl" : undefined}
                px="md"
                py="sm"
                w="full"
              >
                <Text
                  fontSize="xxs"
                  fontWeight="semibold"
                  textColor="secondary"
                >
                  {group.section}
                </Text>
              </Flex>
            )}
            <Flex
              aria-checked={ariaCheckedValue(groupValue)}
              aria-disabled={groupDisabled || undefined}
              aria-expanded={expanded}
              aria-label={group.label}
              aria-level={1}
              dir="column"
              gap="none"
              id={nodeDomId(groupNode)}
              role="treeitem"
              w="full"
            >
              <Flex
                align="center"
                className={classNames(
                  css.row,
                  groupNode === activeNodeId && css.active,
                  groupDisabled && css.disabled,
                )}
                gap="sm"
                minW="none"
                onClick={() => {
                  if (!searching) {
                    toggleGroupExpanded(group.id);
                  }
                }}
                px="md"
                py="sm"
                w="full"
              >
                <Flex
                  aria-hidden="true"
                  onClick={(event) => event.stopPropagation()}
                  shrink={0}
                >
                  <RedoCheckbox
                    disabled={groupDisabled}
                    setValue={(value) => {
                      commitSelected(
                        withItemIdsSelection(
                          selected,
                          itemIds,
                          value !== false,
                          itemsById,
                          disabledIdSet,
                        ),
                      );
                    }}
                    size="sm"
                    tabIndex={-1}
                    value={groupValue}
                  />
                </Flex>
                <Flex
                  align="center"
                  grow={1}
                  justify="space-between"
                  minW="none"
                  overflow="hidden"
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    {group.label}
                  </Text>
                  <Flex align="center" gap="xs" shrink={0}>
                    <RedoBadge
                      color={selectedCount > 0 ? "brand" : "gray"}
                      size="xs"
                      text={`${selectedCount}/${groupItems.length}`}
                    />
                    <RedoIcon
                      className={classNames(
                        css.dropdownIcon,
                        expanded && css.active,
                      )}
                      color="tertiary"
                      Icon={TriangleDown}
                      size={16}
                    />
                  </Flex>
                </Flex>
              </Flex>
              <CheckboxTreeGroupChildren expanded={expanded}>
                {groupItems.map((item) => {
                  const nodeId = itemNodeId(group.id, item.id);
                  const itemDisabled =
                    item.disabled || disabledIdSet.has(item.id);
                  const checked = selected.has(item.id);
                  return (
                    <Flex
                      align="center"
                      aria-checked={checked}
                      aria-disabled={itemDisabled || undefined}
                      aria-label={item.label}
                      aria-level={2}
                      className={classNames(
                        css.row,
                        nodeId === activeNodeId && css.active,
                        itemDisabled && css.disabled,
                      )}
                      gap="sm"
                      id={nodeDomId(nodeId)}
                      key={item.id}
                      minW="none"
                      onClick={() => {
                        if (itemDisabled) {
                          return;
                        }
                        commitSelected(
                          withItemSelection(selected, item.id, !checked),
                        );
                      }}
                      px="md"
                      py="xs"
                      role="treeitem"
                      w="full"
                    >
                      <Flex
                        aria-hidden="true"
                        onClick={(event) => event.stopPropagation()}
                        shrink={0}
                      >
                        <RedoCheckbox
                          disabled={itemDisabled}
                          setValue={(value) => {
                            commitSelected(
                              withItemSelection(
                                selected,
                                item.id,
                                value !== false,
                              ),
                            );
                          }}
                          size="sm"
                          tabIndex={-1}
                          value={checked}
                        />
                      </Flex>
                      <Text fontSize="xs">{item.label}</Text>
                    </Flex>
                  );
                })}
              </CheckboxTreeGroupChildren>
            </Flex>
          </Flex>
        );
      })}
    </Flex>
  );
});
